"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    usePathname,
    useRouter,
} from "next/navigation";

import {
    getLocalizedPath,
    getRouteLanguage,
} from "@/lib/localizedRoutes";

const LanguageContext =
    createContext(null);

const COOKIE_NAME =
    "strikeLocale";

const COOKIE_MAX_AGE =
    60 * 60 * 24 * 365;

const SUPPORTED_LANGUAGES = [
    "hu",
    "en",
];

function isSupportedLanguage(
    language
) {
    return SUPPORTED_LANGUAGES.includes(
        language
    );
}

function getLanguageCookie() {
    if (
        typeof document ===
        "undefined"
    ) {
        return null;
    }

    const cookies =
        document.cookie.split("; ");

    const languageCookie =
        cookies.find(
            (cookie) => {
                return cookie.startsWith(
                    `${COOKIE_NAME}=`
                );
            }
        );

    if (!languageCookie) {
        return null;
    }

    const separatorIndex =
        languageCookie.indexOf("=");

    if (
        separatorIndex <
        0
    ) {
        return null;
    }

    try {
        const value =
            decodeURIComponent(
                languageCookie.slice(
                    separatorIndex +
                        1
                )
            );

        return isSupportedLanguage(
            value
        )
            ? value
            : null;
    } catch {
        return null;
    }
}

function detectBrowserLanguage() {
    if (
        typeof navigator ===
        "undefined"
    ) {
        return "hu";
    }

    const browserLanguages =
        navigator.languages
            ?.length
            ? navigator.languages
            : [
                navigator.language,
            ];

    const primaryLanguage =
        browserLanguages[0]
            ?.toLowerCase() ||
        "";

    return primaryLanguage.startsWith(
        "hu"
    )
        ? "hu"
        : "en";
}

function saveLanguageCookie(
    language
) {
    if (
        typeof document ===
        "undefined"
    ) {
        return;
    }

    const secure =
        window.location
            .protocol ===
        "https:"
            ? "; Secure"
            : "";

    document.cookie =
        `${COOKIE_NAME}=${encodeURIComponent(
            language
        )}` +
        "; Path=/" +
        `; Max-Age=${COOKIE_MAX_AGE}` +
        "; SameSite=Lax" +
        secure;
}

export function LanguageProvider({
    children,
}) {
    const pathname =
        usePathname();

    const router =
        useRouter();

    const routeLanguage =
        getRouteLanguage(
            pathname
        );

    const [
        language,
        setLanguageState,
    ] = useState(
        () =>
            routeLanguage ||
            "hu"
    );

    const [
        isReady,
        setIsReady,
    ] = useState(false);

    useEffect(() => {
        const forcedLanguage =
            getRouteLanguage(
                pathname
            );

        /*
         * Ha az URL-hez fix nyelv tartozik,
         * akkor MINDIG az URL nyer.
         *
         * Nincs redirect.
         *
         * Csak:
         * - átállítjuk a contextet
         * - átállítjuk a html lang attribútumot
         * - frissítjük a cookie-t
         */
        if (
            forcedLanguage
        ) {
            setLanguageState(
                forcedLanguage
            );

            document
                .documentElement
                .lang =
                forcedLanguage;

            saveLanguageCookie(
                forcedLanguage
            );

            setIsReady(
                true
            );

            return;
        }

        /*
         * Olyan oldalakon, amelyeknek
         * nincs külön HU/EN URL-jük,
         * marad a régi működés:
         *
         * cookie -> browser language
         */
        const savedLanguage =
            getLanguageCookie();

        const initialLanguage =
            savedLanguage ||
            detectBrowserLanguage();

        setLanguageState(
            initialLanguage
        );

        document
            .documentElement
            .lang =
            initialLanguage;

        if (
            !savedLanguage
        ) {
            saveLanguageCookie(
                initialLanguage
            );
        }

        setIsReady(
            true
        );
    }, [
        pathname,
    ]);

    const setLanguage =
        useCallback(
            (
                newLanguage
            ) => {
                if (
                    !isSupportedLanguage(
                        newLanguage
                    )
                ) {
                    return;
                }

                saveLanguageCookie(
                    newLanguage
                );

                document
                    .documentElement
                    .lang =
                    newLanguage;

                const localizedPath =
                    getLocalizedPath(
                        pathname,
                        newLanguage
                    );

                /*
                 * Ha ennek az oldalnak
                 * létezik külön nyelvi URL-je,
                 * akkor oda navigálunk.
                 */
                if (
                    localizedPath !==
                    pathname
                ) {
                    setLanguageState(
                        newLanguage
                    );

                    let destination =
                        localizedPath;

                    if (
                        typeof window !==
                        "undefined"
                    ) {
                        destination +=
                            window.location
                                .search;

                        destination +=
                            window.location
                                .hash;
                    }

                    router.push(
                        destination
                    );

                    return;
                }

                /*
                 * Ha nincs külön SEO URL,
                 * maradunk ugyanazon az URL-en,
                 * és csak a tartalom vált.
                 */
                setLanguageState(
                    newLanguage
                );
            },
            [
                pathname,
                router,
            ]
        );

    const contextValue =
        useMemo(
            () => {
                return {
                    language,
                    setLanguage,
                    isReady,
                };
            },
            [
                language,
                setLanguage,
                isReady,
            ]
        );

    return (
        <LanguageContext.Provider
            value={
                contextValue
            }
        >
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context =
        useContext(
            LanguageContext
        );

    if (!context) {
        throw new Error(
            "useLanguage must be used inside LanguageProvider"
        );
    }

    return context;
}